/**
 * EmailErrorI18n (SSOT) — traduce los códigos de error de envío de correo del
 * backend a un mensaje user-facing CLARO y cálido.
 *
 * Problema que resuelve: cuando el envío de correo (Resend) falla, el backend
 * responde 502 con `{error: 'No se pudo enviar el correo', code: 'EMAIL_*'}`.
 * Ese 502 + mensaje genérico, sin traducción, le llegaba al usuario como un
 * error opaco que parecía un fallo de red/CORS. El usuario no entiende qué pasó
 * ni qué hacer.
 *
 * Los 4 códigos (config ausente, API key placeholder, SDK npm ausente, envío
 * rechazado) son todos causa OPERATIVA del lado del servidor — el usuario no
 * hizo nada mal y no puede arreglarlos. Por eso colapsan a UN solo mensaje
 * humano: "el correo no salió por un problema temporal, reintentá o avisá al
 * operador". No exponemos la jerga técnica (SDK, API key) al usuario final.
 *
 * Consumido por front-cashier (credenciales) y front-promoter (activación), que
 * proveen su propio `t()`. La clave i18n vive en el catálogo de cada front.
 */

// Prefijo estable de los códigos de error de email del backend
// (email-resend-sender.js: EMAIL_CONFIG_AUSENTE / EMAIL_API_KEY_PLACEHOLDER /
// EMAIL_SDK_AUSENTE / EMAIL_ENVIO_FALLO). Cualquier EMAIL_* es "el correo no
// salió por un problema del servidor".
const PREFIJO_CODIGO_ERROR_EMAIL = 'EMAIL_';

// Clave i18n única para todos los fallos de envío de correo. Cada front la
// declara en su catálogo con un texto cálido y accionable.
const CLAVE_I18N_FALLO_ENVIO_CORREO = 'errorEmail.noSePudoEnviar';

/**
 * @param {string|null|undefined} codigoBackend - el `code` de la respuesta de error.
 * @returns {boolean} true si es un fallo de envío de correo del servidor.
 */
export function esCodigoErrorDeEnvioDeCorreo(codigoBackend) {
    return typeof codigoBackend === 'string' && codigoBackend.indexOf(PREFIJO_CODIGO_ERROR_EMAIL) === 0;
}

/**
 * Devuelve el mensaje user-facing para un error de envío de correo, o null si el
 * código NO es de email (el caller debe usar su manejo normal en ese caso).
 *
 * @param {string|null|undefined} codigoBackend - el `code` de la respuesta de error.
 * @param {(clave: string) => string} traducir - el `t()` del front consumidor.
 * @returns {string|null} mensaje claro listo para mostrar, o null si no aplica.
 */
export function traducirErrorDeEnvioDeCorreo(codigoBackend, traducir) {
    if (!esCodigoErrorDeEnvioDeCorreo(codigoBackend)) return null;
    return traducir(CLAVE_I18N_FALLO_ENVIO_CORREO);
}
